import { CSSProperties, ReactNode, useEffect, useMemo, useState } from "react";
import { Pagination, Table } from "react-bootstrap";

export interface DataTableColumn<T extends object> {
    text: ReactNode;
    dataField: keyof T & string;
    formatter?: (cell: any, row: T) => ReactNode;
    headerStyle?: () => CSSProperties;
}

interface DataTableProperties<T extends object> {
    columns: DataTableColumn<T>[];
    data: T[];
    keyField: keyof T & string;
    pageSize?: number;
}

const DataTable = <T extends object>({
    columns,
    data,
    keyField,
    pageSize = 20,
}: DataTableProperties<T>) => {
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(data.length / pageSize));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const pageData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return data.slice(start, start + pageSize);
    }, [currentPage, data, pageSize]);

    const renderCell = (value: unknown): ReactNode => {
        if (value === null || value === undefined) {
            return "";
        }

        return typeof value === "string" || typeof value === "number"
            ? value
            : String(value);
    };

    return (
        <>
            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th key={column.dataField} style={column.headerStyle?.()}>
                                {column.text}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {pageData.map((row, index) => (
                        <tr key={String(row[keyField] ?? index)}>
                            {columns.map((column) => {
                                const value = row[column.dataField];

                                return (
                                    <td key={column.dataField}>
                                        {column.formatter
                                            ? column.formatter(value, row)
                                            : renderCell(value)}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
            {totalPages > 1 && (
                <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage((page) => page - 1)} disabled={currentPage === 1} />
                    <Pagination.Item active>{currentPage}</Pagination.Item>
                    <Pagination.Next onClick={() => setCurrentPage((page) => page + 1)} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
            )}
        </>
    );
};

export default DataTable;
